import { useQuery } from '@tanstack/react-query'
import { dashboardApi } from '@/api/dashboard'
import { api } from '@/api/client'
import { useCurrentTenant, useCan } from '@/store/auth'

export type DashboardProfile = 'dev' | 'po' | 'manager'

export function useDashboardData(profile?: DashboardProfile) {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  const can = useCan()

  const canResources = can('recursos', 'read')
  const canChamados = can('chamados', 'read')
  const canFinops = can('finops', 'read')
  const canAudit = can('audit', 'read')

  // Filter permissions based on profile if specified
  const isDevProfile = profile === 'dev'
  const isPoProfile = profile === 'po'
  const isManagerProfile = profile === 'manager'

  // Dev profile: focus on resources, costs, infrastructure, alerts
  const kpis = useQuery({
    queryKey: ['dashboard-kpis', tenantId, profile],
    queryFn: () => dashboardApi.getKpis(tenantId!),
    enabled: !!tenantId && (!profile || isDevProfile || isPoProfile || isManagerProfile),
    staleTime: 60_000,
  })

  const costTrend = useQuery({
    queryKey: ['cost-trend', tenantId, profile],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenantId}/finops/cost-trend?months=6`)
      return res.data
    },
    enabled: !!tenantId && canFinops && (!profile || isDevProfile || isPoProfile || isManagerProfile),
    staleTime: 300_000,
  })

  const costByService = useQuery({
    queryKey: ['dashboard-cost-service', tenantId, profile],
    queryFn: () => dashboardApi.getCostByService(tenantId!),
    enabled: !!tenantId && canFinops && (!profile || isDevProfile || isPoProfile),
    staleTime: 300_000,
  })

  // PO profile: focus on tickets and business metrics
  const audit = useQuery({
    queryKey: ['audit-recent', tenantId, profile],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenantId}/audit?limit=10`)
      return res.data
    },
    enabled: !!tenantId && canAudit && (!profile || isManagerProfile),
    staleTime: 60_000,
  })

  // All profiles: budget for monitoring costs
  const budget = useQuery({
    queryKey: ['budget', tenantId, profile],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenantId}/budget/me`)
      return res.data
    },
    enabled: !!tenantId && canFinops && (!profile || isDevProfile || isPoProfile || isManagerProfile),
    staleTime: 60_000,
  })

  // All profiles: load user preferences for dashboard
  const preferences = useQuery({
    queryKey: ['dashboard-preferences', tenantId],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenantId}/dashboard/preferences`)
      return res.data
    },
    enabled: !!tenantId,
    staleTime: 300_000,
  })

  return {
    tenant,
    tenantId,
    canResources,
    canChamados,
    canFinops,
    canAudit,
    kpis,
    costTrend,
    costByService,
    audit,
    budget,
    preferences,
  }
}
